import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  Trophy,
  Medal,
  Award,
  TrendingUp,
  Users,
  Vote,
  FileText,
  MessageSquare,
  ShieldCheck,
  Filter,
  Star,
} from "lucide-react";
import api, { API_ENDPOINTS } from "../config/api";
import { useAuth } from "../contexts/AuthContext";

const GovernanceLeaderboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    sortBy: "votesCast",
    sortOrder: "desc",
  });

  const { data: leaderboardData, isLoading } = useQuery(
    ["governance", "leaderboard", "full", filters],
    async () => {
      const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.LEADERBOARD, {
        params: filters,
      });
      return data;
    },
    { keepPreviousData: true }
  );

  const leaderboard = leaderboardData?.leaderboard || [];
  const pagination = leaderboardData?.pagination;

  const sortOptions = useMemo(
    () => [
      { value: "votesCast", label: "Votes Cast" },
      { value: "activityPoints", label: "Activity Points" },
      { value: "proposalsSubmitted", label: "Proposals Submitted" },
      { value: "disputesResolved", label: "Disputes Resolved" },
    ],
    []
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === "page" ? value : 1,
    }));
  };

  const getRankBadge = (position) => {
    if (position === 1) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">
          <Trophy className="w-6 h-6" />
        </div>
      );
    }
    if (position === 2) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          <Medal className="w-6 h-6" />
        </div>
      );
    }
    if (position === 3) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
          <Award className="w-6 h-6" />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-semibold">
        {position}
      </div>
    );
  };

  const getTotalScore = (member) => {
    return (
      member.votesCast * 5 +
      member.proposalsSubmitted * 10 +
      member.disputesResolved * 15 +
      member.commentsPosted * 2
    );
  };

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate("/governance")}
            className="btn-outline mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Governance
          </button>
          <h1 className="text-3xl font-heading font-bold text-heading mb-2">
            DAO Leaderboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
            Top contributors shaping the future of Workloob through active
            participation in governance, disputes, and platform improvements.
          </p>
        </div>
      </header>

      <section className="card p-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
            <div>
              <label className="text-xs uppercase text-gray-500 mb-2 block">
                Sort by
              </label>
              <select
                className="input-field"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs uppercase text-gray-500 mb-2 block">
                Order
              </label>
              <select
                className="input-field"
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange("sortOrder", e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div>
              <label className="text-xs uppercase text-gray-500 mb-2 block">
                Per page
              </label>
              <select
                className="input-field"
                value={filters.limit}
                onChange={(e) =>
                  handleFilterChange("limit", parseInt(e.target.value))
                }
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="card p-12 flex flex-col items-center space-y-4">
          <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full" />
          <p className="text-gray-500">Loading leaderboard...</p>
        </div>
      ) : (
        <>
          <section className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <Vote className="w-4 h-4" />
                        Votes
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <Star className="w-4 h-4" />
                        Points
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <FileText className="w-4 h-4" />
                        Proposals
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        Disputes
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Comments
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Total Score
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {leaderboard.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p>No DAO members found yet.</p>
                        <p className="text-sm mt-1">
                          Members need 9+ activity points to appear here.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    leaderboard.map((member, index) => {
                      const position =
                        (pagination.currentPage - 1) * pagination.pageSize +
                        index +
                        1;
                      const totalScore = getTotalScore(member);
                      const isCurrentUser =
                        user?._id === member._id || user?.id === member._id;

                      return (
                        <tr
                          key={member._id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                            isCurrentUser
                              ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500"
                              : ""
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getRankBadge(position)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
                                {member.avatar ? (
                                  <img
                                    src={member.avatar}
                                    alt={member.username}
                                    className="h-10 w-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <span>
                                    {(member.firstName?.[0] ||
                                      member.username?.[0] ||
                                      "U").toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-heading">
                                  {member.firstName && member.lastName
                                    ? `${member.firstName} ${member.lastName}`
                                    : member.username}
                                  {isCurrentUser && (
                                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                      You
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  @{member.username}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-sm font-semibold text-heading">
                              {member.votesCast}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                              {member.activityPoints}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-sm text-heading">
                              {member.proposalsSubmitted}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex flex-col items-center">
                              <span className="text-sm text-heading">
                                {member.disputesRaised} raised
                              </span>
                              <span className="text-xs text-green-600 dark:text-green-400">
                                {member.disputesResolved} resolved
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-sm text-heading">
                              {member.commentsPosted}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-sm font-bold text-accent">
                              {totalScore}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between card px-6 py-4">
              <div className="text-sm text-gray-500">
                Showing{" "}
                {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
                {Math.min(
                  pagination.currentPage * pagination.pageSize,
                  pagination.total
                )}{" "}
                of {pagination.total} members
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="btn-outline text-sm"
                  disabled={pagination.currentPage === 1}
                  onClick={() =>
                    handleFilterChange("page", pagination.currentPage - 1)
                  }
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 7) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 4) {
                      pageNum = i + 1;
                    } else if (
                      pagination.currentPage >=
                      pagination.totalPages - 3
                    ) {
                      pageNum = pagination.totalPages - 6 + i;
                    } else {
                      pageNum = pagination.currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`px-3 py-1.5 text-sm rounded ${
                          pagination.currentPage === pageNum
                            ? "bg-accent text-white"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                        }`}
                        onClick={() => handleFilterChange("page", pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="btn-outline text-sm"
                  disabled={
                    pagination.currentPage === pagination.totalPages
                  }
                  onClick={() =>
                    handleFilterChange("page", pagination.currentPage + 1)
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <section className="card p-6 space-y-3 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
            <h3 className="text-lg font-semibold text-heading flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Scoring System
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <p className="font-medium text-heading mb-2">
                  How points are calculated:
                </p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Vote cast: +5 points</li>
                  <li>Proposal submitted: +10 points</li>
                  <li>Dispute resolved: +15 points</li>
                  <li>Comment posted: +2 points</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-heading mb-2">
                  Leaderboard eligibility:
                </p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Minimum 9 activity points required</li>
                  <li>Ranking updates in real-time</li>
                  <li>Multiple sorting options available</li>
                  <li>Your position is highlighted in blue</li>
                </ul>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default GovernanceLeaderboardPage;
